import { useMutation } from '@apollo/client';

import { TextInput } from 'components/site/layouts/DefaultPageLayout/TextInput';
import { WebButton } from 'elements/button/WebButton';
import {
    RegisterUserByAdmin,
    RegisterUserByAdminVariables
} from 'libs/graphql/mutation/access/__generated__/RegisterUserByAdmin';
import { REGISTER_USER_BY_ADMIN_MUTATION } from 'libs/graphql/mutation/access/accessMutation';
import { EColors, EShadows } from 'libs/styles/styling';
import { EToastType } from 'libs/types/site/IClient';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';
import { VALID_EMAIL_HASH } from 'libs/utils/validation/validation';
import { useState } from 'react';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    onCreated: () => void;
    onCanceled: () => void;
}

export const CreateUserForm = (props: Props): JSX.Element => {
    const [userId, setUserId] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [registerUser] = useMutation<RegisterUserByAdmin, RegisterUserByAdminVariables>(
        REGISTER_USER_BY_ADMIN_MUTATION,
        {
            onCompleted: () => {
                props.onCreated();
            },
            onError: (err) => {
                const r = err.message;
                if (r === 'PASSWORD_NOT_VALID') {
                    userMessage(
                        EToastType.ERROR,
                        'Password is not valid. Try a stronger password.'
                    );
                } else if (r === 'USER_ID_NOT_UNIQUE') {
                    userMessage(EToastType.ERROR, 'User ID already exists');
                } else if (r === 'EMAIL_ALREADY_REGISTERED') {
                    userMessage(EToastType.ERROR, 'Email is already used');
                }
            }
        }
    );

    return (
        <S_CreateUserForm className={props.className}>
            <S_Text>
                Minimum length for username and password is 6 characters.
                <br />
                <br />
                Make sure to remember/store the password to share it with the person. The password
                will not be available for you after you submit the form.
            </S_Text>

            <S_Form
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                    }
                }}
            >
                <S_TextInput
                    value={userId}
                    label="Username"
                    onChange={(value) => {
                        setUserId(value);
                    }}
                />
                <S_TextInput
                    value={email}
                    label="Email"
                    onChange={(value) => {
                        setEmail(value);
                    }}
                />
                <S_TextInput
                    value={password}
                    label="Password"
                    onChange={(value) => {
                        setPassword(value);
                    }}
                    type="text"
                />
                <S_TextInput
                    value={confirmPassword}
                    label="Confirm password"
                    onChange={(value) => {
                        setConfirmPassword(value);
                    }}
                    type="text"
                />

                <S_Buttons>
                    <WebButton
                        text={'Back'}
                        type="secondary"
                        shadow={EShadows.STRONG}
                        onClick={() => {
                            props.onCanceled();
                        }}
                    />
                    <WebButton
                        text={'Create user'}
                        type="primary"
                        shadow={EShadows.STRONG}
                        onClick={() => {
                            registerUser({
                                variables: { userId, email, password, confirmPassword: password }
                            });
                        }}
                        disabled={
                            password.length < 6 ||
                            userId.length < 6 ||
                            !email.match(VALID_EMAIL_HASH)
                        }
                    />
                </S_Buttons>
            </S_Form>
        </S_CreateUserForm>
    );
};

const S_CreateUserForm = styled.div`
    width: 550px;
`;

const S_Text = styled.p`
    font-style: italic;
    color: ${EColors.WHITE};
    margin-bottom: 20px;
`;

const S_Form = styled.form`
    display: flex;
    flex-direction: column;
    width: 350px;
`;

const S_TextInput = styled(TextInput)`
    margin: 20px 0 20px 0;
`;

const S_Buttons = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
`;
